import { ExclamationCircleOutlined } from '@ant-design/icons';
import { message, Modal } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React from 'react';

import { CleanStatusTypeConst } from '../../constants';
import type {
  CleanReservationDetailsResponse,
  CleanReserveList,
  CleanServiceItem,
} from '../../sharedTypes';
import type { FormSubmitProps, OrderGenerateRequest } from './types';

const { confirm } = Modal;

const getItemPrice = (cleanServiceItem: CleanServiceItem | undefined) => {
  switch (cleanServiceItem?.priceInfo.kind) {
    case 'Both':
    case 'TaiwanPay':
      return cleanServiceItem.priceInfo.taiwanPayPrice;

    default:
      return 0;
  }
};

export const getTotalPrice = (
  reservations: CleanReserveList['clean'],
  cleanServiceItems: CleanServiceItem[]
) => {
  const totalPrice = reservations.reduce((totalPrice, reservation) => {
    const singleReservationPrice = reservation.items.reduce((price, item) => {
      if (item.isFreeItem === false && item.quantity !== null) {
        const foundItem = cleanServiceItems.find(
          (cleanServiceItem) => cleanServiceItem._id === item.id
        );
        const itemPrice = getItemPrice(foundItem);
        price = item.quantity * itemPrice + price;
      }
      return price;
    }, 0);
    totalPrice = totalPrice + singleReservationPrice;
    return totalPrice;
  }, 0);

  return totalPrice;
};

export const validateTicketField = (args: { isCheckTicket: boolean }) => {
  const { isCheckTicket } = args;

  if (!isCheckTicket) {
    return Promise.reject(new Error('請勾選扣抵票券'));
  }

  return Promise.resolve();
};

export const validatePaidPayment = (args: {
  isCheckPoint: boolean;
  userPointAmount: number | undefined;
  requiredPointAmount: number | undefined;
}) => {
  const { isCheckPoint, userPointAmount, requiredPointAmount } = args;

  const remainingPointAmount =
    typeof userPointAmount === 'number' &&
    typeof requiredPointAmount === 'number' &&
    userPointAmount - requiredPointAmount;

  if (
    isCheckPoint &&
    typeof remainingPointAmount === 'number' &&
    remainingPointAmount < 0
  ) {
    return Promise.reject(new Error('(餘額不足,無法勾選點數付費)'));
  }

  return Promise.resolve();
};

const submitForm = async (type: 'reserve' | 'pay', props: FormSubmitProps) => {
  const {
    cleanReserveList,
    cleanKindInfo,
    freeItem,
    ticketAmount,
    buyItems,
    requiredPointAmount,
    updateIsWaiting,
    closePaymentInfoModal,
    isCheckPointPay,
    updateIsShowFailedPaymentMessage,
  } = props;

  updateIsWaiting(true);

  if (cleanReserveList && cleanKindInfo) {
    const cleanServiceItems = cleanKindInfo.cleanServiceItems;

    const ticket = cleanServiceItems.find(
      (item) => item.priceInfo.kind === 'Free'
    );

    const amount = getTotalPrice(
      cleanReserveList.clean,
      cleanKindInfo.cleanServiceItems
    );

    const userId = cleanReserveList.userId;

    const cleanBookings = cleanReserveList.clean.map((reservation) => {
      const cleanServiceItems = reservation.items.map((item) => ({
        cleanServiceItemId: item.id,
        count: item.quantity ?? 0,
      }));
      return {
        reservationDate: reservation.date.startOf('date').valueOf(),
        userId,
        communityName: cleanReserveList.communityName,
        householdNumber: cleanReserveList.householdNumber,
        lastMessageSendTime: null,
        cleanKindId: cleanReserveList.cleanKindId,
        cleanServiceItems,
        cleanStatus: [
          {
            type: CleanStatusTypeConst.PaymentPending,
            createdTime: dayjs().valueOf(),
          },
        ],
        cleaner: null,
        cleanStartTime: null,
        cleanStartPhotoUrl: null,
        cleanEndTime: null,
        cleanEndPhotoUrl: null,
        cleanCompleteEditor: null,
        rating: null,
        comment: null,
        memberSignatureImage: null,
      };
    });

    const data: OrderGenerateRequest = {
      order: {
        serviceDifference: {
          kind: 'clean',
          bookingIdList: [],
          requireTickets: {
            ticketId:
              ticket && ticket.priceInfo.kind === 'Free'
                ? ticket.priceInfo.availableTicketId[0]
                : '',
            total: freeItem && ticketAmount ? ticketAmount : 0,
          },
        },
        serviceId: cleanKindInfo.serviceId,
        orderDetailCollectionName: 'cleanBookings',
        orderDetailId: null,
        buyItems: buyItems ?? [],
        lastOrderId: null,
        orderNumber: null,
        userId,
        communityName: cleanReserveList.communityName,
        householdNumber: cleanReserveList.householdNumber,
        amount,
        pointAmount: requiredPointAmount ?? null,
        paymentTime: null,
        paymentType: amount ? '信用卡' : requiredPointAmount ? '點數' : '票券',
      },
      cleanBookings,
      isSupervisor: true,
    };

    try {
      const res = await axios({
        method: 'POST',
        url: '/ezcon/api/createCleanBookingOrder',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        data,
      });
      const { queueId } = res.data;

      const interval = setInterval(async () => {
        try {
          const res = await axios({
            method: 'GET',
            url: `/ezcon/api/checkCleanBookingOrderGeneration/${queueId}`,
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
            timeout: 500,
          });
          clearInterval(interval);
          updateIsWaiting(false);
          if (type === 'reserve') {
            message.success('預約成功!');
            closePaymentInfoModal(true);
          }
          if (type === 'pay') {
            const data = {
              userId,
              orderId: res.data[0].orderId,
              paymentType:
                amount === 0 && isCheckPointPay && requiredPointAmount
                  ? 'point'
                  : 'free',
            };

            try {
              await axios({
                method: 'POST',
                url: '/ezcon/api/payment',
                headers: {
                  authorization: localStorage.getItem('feathers-jwt'),
                },
                data,
              });
              message.success('支付成功!');
              closePaymentInfoModal(true);
            } catch (error) {
              updateIsShowFailedPaymentMessage(true);
            }
          }
        } catch (error) {
          clearInterval(interval);
          updateIsWaiting(false);
          message.error(error.response.data.displayMessage, 5);
        }
      }, 1000);
    } catch (error) {
      updateIsWaiting(false);
      message.error(error.response.data.displayMessage, 5);
    }
  }
};

const showSubmitReservationConfirm = (
  type: 'reserve' | 'pay',
  props: FormSubmitProps
) => {
  confirm({
    title:
      type === 'reserve' ? '確定送出清潔預約？' : '確定立即支付完成清潔預約？',
    icon: <ExclamationCircleOutlined />,
    content: '確認資料無誤!!',
    centered: true,
    onOk() {
      submitForm(type, { ...props });
    },
  });
};

export const handleSubmitReservationButtonClick = async (
  type: 'reserve' | 'pay',
  props: FormSubmitProps
) => {
  const { form } = props;
  try {
    await form.validateFields();
    showSubmitReservationConfirm(type, props);
  } catch (error) {
    return;
  }
};

export const checkIsDisablePayNowButton = (args: {
  cleanReserveList: CleanReserveList | undefined;
  cleanKindInfo: CleanReservationDetailsResponse | undefined;
  isCheckTicket: boolean;
  isCheckPoint: boolean;
  userPointAmount: number | undefined;
  requiredPointAmount: number | undefined;
}) => {
  const {
    cleanReserveList,
    cleanKindInfo,
    isCheckTicket,
    isCheckPoint,
    userPointAmount,
    requiredPointAmount,
  } = args;

  const isCashPriceGreaterThanZero =
    cleanReserveList &&
    cleanKindInfo &&
    getTotalPrice(cleanReserveList.clean, cleanKindInfo.cleanServiceItems) > 0;
  const isNotCheckAnyCheckbox = !isCheckTicket && !isCheckPoint;
  const isPointInvalid =
    isCheckPoint &&
    typeof userPointAmount === 'number' &&
    typeof requiredPointAmount === 'number' &&
    requiredPointAmount === 0 &&
    userPointAmount - requiredPointAmount < 0;

  return isCashPriceGreaterThanZero || isNotCheckAnyCheckbox || isPointInvalid;
};
